import React, { Dispatch, FC, SetStateAction, useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Grid, Card, CardHeader, CardContent, Link, Typography, makeStyles } from '@material-ui/core';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import MovieCreationIcon from '@material-ui/icons/MovieCreation';
import EditIcon from '@material-ui/icons/Edit';
import DescriptionIcon from '@material-ui/icons/Description';
import { useFeatures } from 'flagged';
import Highlights from '../common/Highlights';
import { DailyTestGame, Game, LoadingOptions, PaginationOptions, USE_DRM } from '../../utils/types';
import AiringsEditor from './AiringsEditor';
import FersEditor from './FersEditor';
import SimpleTextInfo from '../common/SimpleTextInfo';
import TeamList from '../common/TeamList';
import { getGameHighlights } from '../games/apiQueries';
import Offsets from './Offsets';
import NotificationsDialog from '../notifications/NotificationsDialog';
import awsConfig from '../../aws-exports';
import { useAlertContext } from '../../context/alertContext';
import { PlayCircleFilled } from '@material-ui/icons';
import { ThreeDots } from 'react-loader-spinner';
import BlockIcon from '@material-ui/icons/Block';
import { getJwtToken } from '../common/Auth/JwtHelper';
import { fetchAppConfigByProfileName } from '../common/Appconfig';
import CopyText from '../common/CopyText';
import StatusChip, { statusChipStyles } from '../common/StatusChip';
import dayjs from 'dayjs';
import FarsideViewer from './FarsideViewer';
import { ConfigurableLicenseEndpointSelector } from '../common/LicenseEndpointSelector';

const useStyles = makeStyles((theme) => ({
	gameActionLink: {
		color: theme.palette.secondary.main,
		display: 'flex',
		marginBottom: theme.spacing(2),
		[theme.breakpoints.up('sm')]: {
			justifyContent: 'flex-end'
		},
		[theme.breakpoints.down('sm')]: {
			marginTop: theme.spacing(2)
		}
	},
	linkText: {
		marginLeft: theme.spacing(0.5)
	}
}));

const isDailyTestGame = (gameId: string, dailyTestGameIDs: string[]): boolean => {
	return JSON.stringify(dailyTestGameIDs).includes(gameId);
};

export const GameDetails: FC<GameDetailsProps> = (props) => {
	const {
		airings,
		airingsPaginationOptions,
		fers,
		fersLoadingOptions,
		id,
		sport,
		startTime,
		teams,
		title,
		gameStatus,
		offsets,
		league,
		useDrm,
		setAutoRefetch
	} = props;

	const { accountName, defaultChannelName, run_daily_test_url: runDailyTestUrl } = awsConfig;

	const classes = useStyles();
	const statusChipClasses = statusChipStyles();
	const { notifications } = useFeatures();
	const sportKey = sport?.toLowerCase() ?? '';
	const { setAlert } = useAlertContext();

	const [progressionsModalOpen, setProgressionsModalOpen] = useState<boolean>(false);
	const [showDailyTesting, setShowDailyTesting] = useState<boolean>(true);

	useEffect(() => {
		const getAppConfigs = async (): Promise<void> => {
			const dailyTestGameConfigsProfile = await fetchAppConfigByProfileName('daily-test-game-configs');
			if (!dailyTestGameConfigsProfile) {
				return;
			}
			const dailyTestObjects = JSON.parse(dailyTestGameConfigsProfile);
			const appConfigByAccount = dailyTestObjects[accountName] ?? dailyTestObjects['development'] ?? {};
			const dailyTestGames = sport
				? (appConfigByAccount[sport.toLowerCase()] as DailyTestGame[])
				: (Object.values(appConfigByAccount) as DailyTestGame[][]).flat();
			setRecapDailyTestGameIDs(dailyTestGames.map((game) => game.id));
		};
		getAppConfigs();
	}, [sport, accountName]);
	const [recapDailyTestGameIDs, setRecapDailyTestGameIDs] = useState<string[]>([]);

	const runDailyTest = async (id: string, sport: string | null | undefined): Promise<void> => {
		const jwtToken = await getJwtToken();

		fetch(runDailyTestUrl, {
			method: 'POST',
			body: JSON.stringify({
				gameId: id,
				sport: sport
			}),
			headers: {
				Authorization: `Bearer ${jwtToken}`,
				'Content-Type': 'application/json'
			}
		})
			.then((response) => response.json())
			.then((data) => {
				if (data.message) {
					setAlert('Daily test started, refreshing page');
					window.location.reload();
				} else if (data.error) {
					setAlert('Error running daily test lambda, please contact the SPEx team.');
					setShowDailyTesting(true);
				}
			})
			.catch((err) => {
				setAlert(`Error in running daily test, ${err}`);
				setShowDailyTesting(true);
			});
	};

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Card>
					<CardHeader title='Game details' />
					<CardContent>
						<Grid container>
							<Grid container item xs={12} sm={8} direction='column' spacing={2}>
								<SimpleTextInfo label='ID' value={<CopyText>{id}</CopyText>} to={`/games/${encodeURIComponent(id)}`} />
								<SimpleTextInfo label='Title' value={<CopyText>{title || 'not specified'}</CopyText>} />
								<SimpleTextInfo label='Sport (League)' value={`${sport || 'not specified'} (${league?.name || 'not specified'})`} />
								<SimpleTextInfo
									label='Game Status'
									value={<StatusChip className={statusChipClasses.smallChip} status={gameStatus || 'not specified'} />}
								/>
								<SimpleTextInfo
									label='Start time'
									value={
										startTime && dayjs(startTime).isValid() ? (
											<>
												<strong>{dayjs(startTime).format('DD MMM YYYY')}</strong> {dayjs(startTime).format('HH:mm')}
											</>
										) : (
											'Unknown'
										)
									}
								/>
								{teams?.items && sportKey !== 'f1' ? <TeamList teams={teams?.items} /> : null}
							</Grid>

							<Grid item xs={12} sm={4}>
							{ airings && airings?.items?.length > 0 && (
								<ConfigurableLicenseEndpointSelector
									instanceId="games-player"
								label="DRM Endpoint"
								variant="button"
								className={classes.gameActionLink}
								/>
							)}
							<Link component={RouterLink} to={`/games/${id}/edit`} className={classes.gameActionLink}>
								<EditIcon />
								<Typography className={classes.linkText}>Edit</Typography>
								</Link>

								{sportKey === 'football' && notifications && (
									<NotificationsDialog gameId={id} startTime={startTime} league={league?.name} teams={teams?.items} classes={classes} />
								)}

								{sportKey === 'golf' && (
									<Link component={RouterLink} to={`/games/${id}/compose-highlight`} className={classes.gameActionLink}>
										<PlaylistAddIcon />
										<Typography className={classes.linkText}>Compose Highlight</Typography>
									</Link>
								)}

								{sportKey === 'golf' && airings && airings?.items?.length > 0 && (
									<>
										<Link component={RouterLink} to={`/games/${id}/editorial-highlight`} className={classes.gameActionLink}>
											<MovieCreationIcon />
											<Typography className={classes.linkText}>Editorial Clips</Typography>
										</Link>
										<Link component={RouterLink} to={`/games/${id}/excluded-clips`} className={classes.gameActionLink}>
											<BlockIcon />
											<Typography className={classes.linkText}>Excluded Clips</Typography>
										</Link>
									</>
								)}
								{['americanfootball', 'football', 'basketball'].includes(sportKey) && (
									<Link className={classes.gameActionLink} onClick={(): void => setProgressionsModalOpen(true)} href='#'>
										<DescriptionIcon />
										<Typography className={classes.linkText}>View Progressions</Typography>
									</Link>
								)}

								{isDailyTestGame(id, recapDailyTestGameIDs) &&
									(showDailyTesting ? (
										<div style={{ cursor: 'pointer' }}>
											<Link
												data-testid='run-daily-test'
												className={classes.gameActionLink}
												onClick={(): void => {
													setShowDailyTesting(false);
													runDailyTest(id, sport);
												}}>
												<PlayCircleFilled />
												<Typography className={classes.linkText}>Run Daily Test</Typography>
											</Link>
										</div>
									) : (
										<div style={{ color: 'grey', float: 'right' }}>
											<ThreeDots
												color='#FF0000'
												height='30'
												width='30'
												wrapperStyle={{
													display: 'inline-block',
													'margin-right': '10px',
													'vertical-align': 'middle'
												}}
											/>
											<Typography style={{ display: 'inline-block', verticalAlign: 'middle' }}>Running Daily Test (auto-refresh)</Typography>
										</div>
									))}
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			</Grid>
			<Grid item xs={12}>
				<AiringsEditor
					airings={airings?.items || []}
					defaultChannelName={defaultChannelName}
					gameId={id}
					gameStartTime={startTime ?? null}
					paginationOptions={airingsPaginationOptions}
					sport={sport}
					useDrm={useDrm}
					setAutoRefetch={setAutoRefetch}
				/>
			</Grid>
			<Grid item xs={12}>
				<FersEditor fers={fers || []} gameId={id} loadingOptions={fersLoadingOptions} gameStartTime={startTime ?? null} />
			</Grid>
			{sportKey === 'football' && (
				<Grid item xs={12} lg={12}>
					<Offsets offsets={offsets?.items ?? []} sport={sportKey} />
				</Grid>
			)}
			{sportKey === 'americanfootball' && (
				<Grid item xs={12} lg={12}>
					<Offsets offsets={offsets?.items ?? []} sport={sportKey} />
				</Grid>
			)}
			{sportKey === 'golf' && (
				<Grid item xs={12}>
					<Highlights id={id} query={getGameHighlights} queryPath='getGame' />
				</Grid>
			)}
			<FarsideViewer id={id} sport={sportKey} open={progressionsModalOpen} setOpen={setProgressionsModalOpen} icon={<DescriptionIcon />} />
		</Grid>
	);
};

type GameDetailsProps = Game & { airingsPaginationOptions: PaginationOptions } & { fersLoadingOptions: LoadingOptions } & { useDrm: USE_DRM } & {
	setAutoRefetch: Dispatch<SetStateAction<boolean>>;
};

export default GameDetails;
